// Blanko Pembayaran AP — bentuk dokumen mengikuti blanko Non PO
// (`features/finance/kas-bank/blanko.js`) supaya kedua bukti keluaran Finance
// terlihat seragam, tetapi sumber datanya endpoint AP:
//   GET Accounting/Mon/PembayaranAP/Cetak/:id
//
// Susunan debet/kredit sengaja disamakan PERSIS dengan voucher AP yang sudah
// ada (`pembayaran-ap/vocher.js`) supaya satu transaksi tak pernah tercetak
// dengan jurnal berbeda di dua dokumen:
//   baris 1  DetailAccount    (D)  sebesar NilaiBayar
//   baris 2  DetailAccount2   (K)  sebesar Nominal        <- kas/bank
//   baris 3  Downpayment      (D)  bila DownpaymentCashJournalID terisi
//   sisanya  Data[]           (D)  rincian tambahan
import React from "react";
import {
  Document,
  Image,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchGet } from "../../../fetchers";
import { Colors } from "../../../assets/Colors";
import LOGO_RKS from "../../../assets/logo/LOGO_RKS.png";
import angkaTerbilang from "@develoka/angka-terbilang-js";
import { useUserBisnisUnit } from "../../../hooks/useUserBisnisUnit";

const SO_UNIT = "U0002";

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 10, fontFamily: "Helvetica" },
  header: {
    marginBottom: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logo: { width: 120, height: 50, objectFit: "contain" },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 20,
    textTransform: "uppercase",
    letterSpacing: 1,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 8,
  },
  infoContainer: {
    flexDirection: "row",
    marginBottom: 20,
    justifyContent: "space-between",
  },
  leftColumn: { width: "45%" },
  rightColumn: { width: "45%", marginLeft: "auto" },
  infoRow: { flexDirection: "row", marginBottom: 5, alignItems: "flex-start" },
  infoLabel: { width: 90, fontSize: 9 },
  infoSeparator: { width: 8, fontSize: 9, textAlign: "center" },
  infoValue: { flex: 1, fontSize: 9 },

  table: { marginTop: 20, borderWidth: 1, borderColor: "#000", width: "100%" },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    minHeight: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    minHeight: 22,
  },
  colNo: {
    width: "6%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "center",
  },
  colAccountName: {
    width: "34%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
  },
  colDK: {
    width: "6%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "center",
  },
  colAccountNo: {
    width: "14%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "center",
  },
  colAmountUSD: {
    width: "20%",
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    textAlign: "right",
  },
  colAmountIDR: { width: "20%", padding: 4, fontSize: 8, textAlign: "right" },

  totalRow: {
    flexDirection: "row",
    backgroundColor: "#f9f9f9",
    minHeight: 24,
    borderTopWidth: 2,
    borderTopColor: "#000",
  },
  totalLabel: {
    width: "60%",
    padding: 4,
    fontSize: 9,
    textAlign: "right",
    borderRightWidth: 1,
    borderRightColor: "#000",
  },
  totalAmount: {
    width: "20%",
    padding: 4,
    fontSize: 9,
    textAlign: "right",
    borderRightWidth: 1,
    borderRightColor: "#000",
  },
  totalAmountLast: { width: "20%", padding: 4, fontSize: 9, textAlign: "right" },

  terbilang: {
    marginTop: 15,
    fontSize: 9,
    fontStyle: "italic",
    borderTopWidth: 0.5,
    borderTopColor: "#999",
    paddingTop: 8,
  },

  signatureContainer: {
    flexDirection: "row",
    marginTop: 50,
    justifyContent: "space-around",
  },
  signatureBox: { width: 120, alignItems: "center" },
  signatureLabel: { fontSize: 8, marginBottom: 5 },
  signatureLine: {
    width: "100%",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginTop: 30,
  },
  signatureName: { fontSize: 8, marginTop: 5 },
});

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return isNaN(d.getTime())
    ? String(dateStr)
    : d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

// Ribuan bertitik, tanpa "Rp" — mengikuti blanko Non PO.
const formatCurrency = (value) => {
  const num = parseFloat(value || 0);
  if (!num) return "0";
  return num
    .toLocaleString("id-ID", { maximumFractionDigits: 2, minimumFractionDigits: 0 })
    .replace(/,/g, ".");
};

const BlankoAPPDF = ({ data }) => {
  if (!data) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Data tidak ditemukan</Text>
        </Page>
      </Document>
    );
  }

  const rincian = Array.isArray(data.Data) ? data.Data : [];
  const adaDownpayment = Boolean(data.DownpaymentCashJournalID);

  // Total blanko = NilaiBayar (nilai yang benar-benar dibayarkan), sama seperti
  // voucher AP. Rincian Data[] bisa bernilai negatif (mis. pembulatan), jadi
  // menjumlahkannya sendiri akan menghasilkan angka yang berbeda dari kas keluar.
  const total = parseFloat(data.NilaiBayar || 0);

  // Kas atau bank ditentukan dari JenisPembayaran ("Bank IDR", "Kas Kecil", ...).
  const isBank = (data.JenisPembayaran || "").toLowerCase().includes("bank");
  const title = isBank ? "BUKTI BANK KELUAR" : "BUKTI KAS KELUAR";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image style={styles.logo} src={LOGO_RKS} />
          <Text style={{ fontSize: 10, color: "#666" }}>No. Dok: F-KEU-001</Text>
        </View>

        <Text style={styles.title}>{title}</Text>

        <View style={styles.infoContainer}>
          <View style={styles.leftColumn}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Dibayarkan Kepada</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{data.DibayarKepada || "-"}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Alamat</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{data.Alamat || "-"}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Telepon</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{data.Telepon || "-"}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Keterangan</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{data.Keterangan || "-"}</Text>
            </View>
          </View>

          <View style={styles.rightColumn}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>No. Bukti</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{data.PembayaranAPID || "-"}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Tanggal</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{formatDate(data.Tanggal)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Bank/Kas</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{data.DetailAccount2 || "-"}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>No. Cek/Giro</Text>
              <Text style={styles.infoSeparator}>:</Text>
              <Text style={styles.infoValue}>{data.NoCek || "-"}</Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.colNo}>No</Text>
            <Text style={styles.colAccountName}>Nama Perkiraan</Text>
            <Text style={styles.colDK}>D/K</Text>
            <Text style={styles.colAccountNo}>No Perkiraan</Text>
            <Text style={styles.colAmountUSD}>Jumlah (USD)</Text>
            <Text style={styles.colAmountIDR}>Jumlah (Rp)</Text>
          </View>

          {/* 1 — akun beban/persediaan (debet) */}
          <View style={styles.tableRow}>
            <Text style={styles.colNo}>1</Text>
            <Text style={styles.colAccountName}>{data.DetailAccount || "-"}</Text>
            <Text style={styles.colDK}>D</Text>
            <Text style={styles.colAccountNo}>{data.DetailAccountID || "-"}</Text>
            <Text style={styles.colAmountUSD}>0</Text>
            <Text style={styles.colAmountIDR}>{formatCurrency(data.NilaiBayar)}</Text>
          </View>

          {/* 2 — kas/bank (kredit) */}
          <View style={styles.tableRow}>
            <Text style={styles.colNo}>2</Text>
            <Text style={styles.colAccountName}>{data.DetailAccount2 || "-"}</Text>
            <Text style={styles.colDK}>K</Text>
            <Text style={styles.colAccountNo}>{data.DetailPembayaran || "-"}</Text>
            <Text style={styles.colAmountUSD}>0</Text>
            <Text style={styles.colAmountIDR}>{formatCurrency(data.Nominal)}</Text>
          </View>

          {/* 3 — uang muka, hanya bila transaksi ini memotong downpayment */}
          {adaDownpayment && (
            <View style={styles.tableRow}>
              <Text style={styles.colNo}>3</Text>
              <Text style={styles.colAccountName}>
                {data.DownpaymentAccountName || data.DownpaymentAccountID || "-"}
              </Text>
              <Text style={styles.colDK}>D</Text>
              <Text style={styles.colAccountNo}>{data.DownpaymentAccountID || "-"}</Text>
              <Text style={styles.colAmountUSD}>0</Text>
              <Text style={styles.colAmountIDR}>
                {formatCurrency(data.DownpaymentAmount)}
              </Text>
            </View>
          )}

          {/* Rincian tambahan (PPN, pembulatan, potongan, ...) */}
          {rincian.map((row, idx) => (
            <View style={styles.tableRow} key={idx}>
              <Text style={styles.colNo}>{idx + (adaDownpayment ? 4 : 3)}</Text>
              <Text style={styles.colAccountName}>{row.DetailAccount || "-"}</Text>
              <Text style={styles.colDK}>D</Text>
              <Text style={styles.colAccountNo}>{row.Account || "-"}</Text>
              <Text style={styles.colAmountUSD}>0</Text>
              <Text style={styles.colAmountIDR}>{formatCurrency(row.Nominal)}</Text>
            </View>
          ))}

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>TOTAL</Text>
            <Text style={styles.totalAmount}>0</Text>
            <Text style={styles.totalAmountLast}>{formatCurrency(total)}</Text>
          </View>
        </View>

        <Text style={styles.terbilang}>
          Terbilang : {angkaTerbilang(Math.round(Math.abs(total)))} rupiah
        </Text>

        <View style={styles.signatureContainer}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Diterima oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{data.DibayarKepada || "-"}</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Dibuat oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>_________________</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Diperiksa oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>_________________</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Disetujui oleh,</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>_________________</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const Pembungkus = ({ children }) => (
  <div className="md:ml-64">
    <div
      style={{ background: Colors.primary }}
      className="pt-14 pb-16 px-3 md:px-8 h-auto"
    />
    <div
      className="px-3 md:px-8"
      style={{ height: "calc(100vh - 150px)", marginTop: "-100px" }}
    >
      {children}
    </div>
  </div>
);

const BlankoPembayaranAP = () => {
  const { id } = useParams();
  // User SO hanya boleh mencetak blanko dokumen unitnya sendiri (halaman ini bisa
  // dicapai lewat URL langsung). Sejalan dgn voucher AP & report Pembayaran AP.
  const { bisnisUnitID: userBisnisUnit, resolved: buResolved } =
    useUserBisnisUnit();

  const { data: resp, isLoading, error } = useQuery({
    queryKey: ["blankoAP", id],
    queryFn: () => fetchGet("Accounting/Mon/PembayaranAP/Cetak/" + id),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Pembungkus>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">Memuat data blanko...</p>
          </div>
        </div>
      </Pembungkus>
    );
  }

  if (error) {
    return (
      <Pembungkus>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p>Gagal memuat blanko: {error.message}</p>
            </div>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Kembali
            </button>
          </div>
        </div>
      </Pembungkus>
    );
  }

  const rowBisnisUnit = resp?.data?.BisnisUnitID || "";
  const isForeignUnit = rowBisnisUnit !== "" && rowBisnisUnit !== SO_UNIT;
  const accessDenied = userBisnisUnit === SO_UNIT && isForeignUnit;
  // Sesi lama: BU user baru diketahui setelah fetch — dokumen unit lain ditahan
  // dulu, bukan ditolak (user HO tetap boleh mencetaknya).
  if (accessDenied || (!buResolved && isForeignUnit)) {
    return (
      <Pembungkus>
        <div className="flex items-center justify-center h-full">
          <div className="max-w-md px-6 text-center">
            {accessDenied ? (
              <>
                <p className="text-lg font-bold text-gray-800 mb-2">Akses Ditolak</p>
                <p className="text-sm text-gray-600 mb-4">
                  Blanko {id || "ini"} milik Bisnis Unit lain. Akun Anda hanya
                  dapat membuka dokumen SO.
                </p>
                <button
                  onClick={() => window.history.back()}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Kembali
                </button>
              </>
            ) : (
              <p className="text-gray-600">Memeriksa hak akses...</p>
            )}
          </div>
        </div>
      </Pembungkus>
    );
  }

  const item = resp?.data;

  if (!item || !item.PembayaranAPID) {
    return (
      <Pembungkus>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-gray-500 mb-4">
              Data blanko untuk {id} tidak tersedia
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Kembali
            </button>
          </div>
        </div>
      </Pembungkus>
    );
  }

  return (
    <Pembungkus>
      <PDFViewer
        style={{
          width: "100%",
          height: "100%",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
        showToolbar={true}
      >
        <BlankoAPPDF data={item} />
      </PDFViewer>
    </Pembungkus>
  );
};

export default BlankoPembayaranAP;
